// client/src/coms/views/canvas_parts/canvas_room_view_parts/modelFaceSelection.js
//
// Pure helpers for Blender-style face and material selection on imported
// models. Selections use the same shape as the existing 3D lasso selection:
// a Map of mesh uuid -> Set of triangle indices. Nothing here touches
// three.js, so the logic is unit tested directly.

export const FACE_SELECTION_SHAPE = 'faces';

export function createFaceSelection(selectedFaces = new Map(), options = {}) {
  return {
    shape: FACE_SELECTION_SHAPE,
    kind: options.kind === 'material' ? 'material' : 'faces',
    selectedFaces,
    boundaryFaces: new Map(),
    surfaceSamples: [],
    closed: true,
    inverted: Boolean(options.inverted),
  };
}

export function cloneFaceMap(source) {
  const result = new Map();
  if (!(source instanceof Map)) return result;
  source.forEach((faces, meshUuid) => {
    if (faces && faces.size) result.set(meshUuid, new Set(faces));
  });
  return result;
}

export function countSelectedFaces(faceMap) {
  if (!(faceMap instanceof Map)) return 0;
  let total = 0;
  faceMap.forEach((faces) => { total += faces ? faces.size : 0; });
  return total;
}

export function isFaceSelected(faceMap, meshUuid, faceIndex) {
  const faces = faceMap instanceof Map ? faceMap.get(meshUuid) : null;
  return Boolean(faces && faces.has(faceIndex));
}

// mode: 'replace' (click), 'add', 'remove', or 'toggle' (Shift-click).
// Returns a new Map; the input is never mutated, so undo-friendly callers
// can keep the previous selection.
export function applyFacesToSelection(faceMap, meshUuid, faceIndices, mode = 'add') {
  const indices = Array.from(faceIndices || []).filter((index) => Number.isInteger(index) && index >= 0);
  const next = mode === 'replace' ? new Map() : cloneFaceMap(faceMap);
  if (!meshUuid || !indices.length) return next;
  const faces = next.get(meshUuid) || new Set();
  indices.forEach((faceIndex) => {
    if (mode === 'remove') faces.delete(faceIndex);
    else if (mode === 'toggle') {
      if (faces.has(faceIndex)) faces.delete(faceIndex);
      else faces.add(faceIndex);
    } else faces.add(faceIndex);
  });
  if (faces.size) next.set(meshUuid, faces);
  else next.delete(meshUuid);
  return next;
}

// Breadth-first flood over triangle adjacency. `canEnter(face)` lets callers
// stop at material boundaries.
export function collectConnectedFaces(seedFaces, adjacency, canEnter = null) {
  const result = new Set();
  if (!adjacency) return result;
  const queue = [];
  Array.from(seedFaces || []).forEach((face) => {
    if (!Number.isInteger(face) || face < 0 || face >= adjacency.length || result.has(face)) return;
    result.add(face);
    queue.push(face);
  });
  for (let cursor = 0; cursor < queue.length; cursor += 1) {
    const neighbours = adjacency[queue[cursor]];
    if (!neighbours) continue;
    neighbours.forEach((neighbour) => {
      if (result.has(neighbour)) return;
      if (typeof canEnter === 'function' && !canEnter(neighbour)) return;
      result.add(neighbour);
      queue.push(neighbour);
    });
  }
  return result;
}

// One ring of neighbouring faces (Blender: Select More).
export function growFaceSet(faces, adjacency) {
  const result = new Set(faces || []);
  if (!adjacency) return result;
  Array.from(faces || []).forEach((face) => {
    const neighbours = adjacency[face];
    if (neighbours) neighbours.forEach((neighbour) => result.add(neighbour));
  });
  return result;
}

// Removes faces on the selection edge (Blender: Select Less).
export function shrinkFaceSet(faces, adjacency) {
  const source = new Set(faces || []);
  const result = new Set();
  if (!adjacency) return result;
  source.forEach((face) => {
    const neighbours = adjacency[face];
    if (!neighbours) return;
    let interior = true;
    neighbours.forEach((neighbour) => {
      if (!source.has(neighbour)) interior = false;
    });
    if (interior) result.add(face);
  });
  return result;
}

// geometry.groups entries are { start, count, materialIndex } measured in
// index-buffer elements, three per triangle.
export function getFaceMaterialIndex(groups, faceIndex) {
  if (!Array.isArray(groups) || !groups.length) return 0;
  const element = faceIndex * 3;
  for (const group of groups) {
    const start = Number(group && group.start) || 0;
    const count = Number(group && group.count);
    const end = Number.isFinite(count) ? start + count : Infinity;
    if (element >= start && element < end) return Number(group.materialIndex) || 0;
  }
  return -1;
}

export function collectFacesWithMaterialIndex(groups, triangleCount, materialIndex) {
  const result = new Set();
  const safeCount = Math.max(0, Math.floor(Number(triangleCount) || 0));
  if (!Array.isArray(groups) || !groups.length) {
    if (materialIndex === 0) for (let face = 0; face < safeCount; face += 1) result.add(face);
    return result;
  }
  groups.forEach((group) => {
    if ((Number(group && group.materialIndex) || 0) !== materialIndex) return;
    const firstFace = Math.floor((Number(group.start) || 0) / 3);
    const groupCount = Number(group.count);
    const lastFace = Number.isFinite(groupCount)
      ? Math.min(safeCount, Math.ceil(((Number(group.start) || 0) + groupCount) / 3))
      : safeCount;
    for (let face = firstFace; face < lastFace; face += 1) result.add(face);
  });
  return result;
}

// Mesh uuids are regenerated every time a model loads. Persisted selection
// restrictions are keyed by the mesh's position in traversal order plus its
// name, which is stable for the same model file.
export function buildStableMeshKey(traversalIndex, name) {
  return `${Math.max(0, Math.floor(Number(traversalIndex) || 0))}:${String(name || '')}`;
}

export function serializeSelectionSnapshot(snapshot, meshKeyByUuid) {
  if (!snapshot || !(snapshot.selectedFaces instanceof Map) || !(meshKeyByUuid instanceof Map)) return null;
  const encodeMap = (faceMap) => {
    const entries = [];
    if (!(faceMap instanceof Map)) return entries;
    faceMap.forEach((faces, meshUuid) => {
      const meshKey = meshKeyByUuid.get(meshUuid);
      if (!meshKey || !faces || !faces.size) return;
      entries.push({ meshKey, faces: Array.from(faces).sort((a, b) => a - b) });
    });
    return entries;
  };
  const selected = encodeMap(snapshot.selectedFaces);
  if (!selected.length && !snapshot.inverted) return null;
  return {
    version: 1,
    inverted: Boolean(snapshot.inverted),
    selected,
    boundary: encodeMap(snapshot.boundaryFaces),
  };
}

export function deserializeSelectionSnapshot(serialized, meshUuidByKey) {
  if (!serialized || typeof serialized !== 'object' || !(meshUuidByKey instanceof Map)) return null;
  const decodeEntries = (entries) => {
    const result = new Map();
    (Array.isArray(entries) ? entries : []).forEach((entry) => {
      const meshUuid = meshUuidByKey.get(String(entry && entry.meshKey));
      if (!meshUuid) return;
      const faces = new Set((Array.isArray(entry.faces) ? entry.faces : []).filter((face) => Number.isInteger(face) && face >= 0));
      if (faces.size) result.set(meshUuid, faces);
    });
    return result;
  };
  const selectedFaces = decodeEntries(serialized.selected);
  if (!selectedFaces.size && !serialized.inverted) return null;
  const selection = createFaceSelection(selectedFaces, { inverted: serialized.inverted });
  selection.boundaryFaces = decodeEntries(serialized.boundary);
  return selection;
}

// --- UV overlap detection -------------------------------------------------
//
// A texture can only isolate faces whose UV islands do not share texels.
// Rasterise UV triangles into a coarse grid (sampling cell centres so faces
// that merely share an edge do not count) and report unselected faces that
// land on texels already used by the selection.

function rasteriseTriangle(grid, resolution, ax, ay, bx, by, cx, cy, visit, stopOnHit = false) {
  const minX = Math.max(0, Math.floor(Math.min(ax, bx, cx) * resolution));
  const maxX = Math.min(resolution - 1, Math.ceil(Math.max(ax, bx, cx) * resolution));
  const minY = Math.max(0, Math.floor(Math.min(ay, by, cy) * resolution));
  const maxY = Math.min(resolution - 1, Math.ceil(Math.max(ay, by, cy) * resolution));
  const area = ((bx - ax) * (cy - ay)) - ((by - ay) * (cx - ax));
  if (Math.abs(area) < 1e-14) return false;
  let hit = false;
  for (let y = minY; y <= maxY; y += 1) {
    const py = (y + 0.5) / resolution;
    for (let x = minX; x <= maxX; x += 1) {
      const px = (x + 0.5) / resolution;
      const w0 = (((bx - px) * (cy - py)) - ((by - py) * (cx - px))) / area;
      const w1 = (((cx - px) * (ay - py)) - ((cy - py) * (ax - px))) / area;
      const w2 = 1 - w0 - w1;
      if (w0 <= 0 || w1 <= 0 || w2 <= 0) continue;
      if (visit(grid, (y * resolution) + x)) {
        hit = true;
        if (stopOnHit) return true;
      }
    }
  }
  return hit;
}

// meshes: [{ uuid, triangleCount, getUvTriangle(face) -> [ax, ay, bx, by, cx, cy] | null,
//            isSelected(face) -> boolean }]
export function findUvOverlapWithSelection(meshes, resolution = 512) {
  const safeResolution = Math.max(16, Math.min(2048, Math.floor(Number(resolution) || 512)));
  const grid = new Uint8Array(safeResolution * safeResolution);
  const markSelected = (cells, cell) => { cells[cell] = 1; return true; };
  const testShared = (cells, cell) => cells[cell] === 1;
  let selectedCells = 0;

  (meshes || []).forEach((mesh) => {
    for (let face = 0; face < mesh.triangleCount; face += 1) {
      if (!mesh.isSelected(face)) continue;
      const uv = mesh.getUvTriangle(face);
      if (uv) rasteriseTriangle(grid, safeResolution, ...uv, markSelected);
    }
  });
  for (let cell = 0; cell < grid.length; cell += 1) if (grid[cell]) selectedCells += 1;

  let overlappingFaces = 0;
  const overlappingMeshes = new Set();
  if (selectedCells) {
    (meshes || []).forEach((mesh) => {
      for (let face = 0; face < mesh.triangleCount; face += 1) {
        if (mesh.isSelected(face)) continue;
        const uv = mesh.getUvTriangle(face);
        if (uv && rasteriseTriangle(grid, safeResolution, ...uv, testShared, true)) {
          overlappingFaces += 1;
          overlappingMeshes.add(mesh.uuid);
        }
      }
    });
  }
  return {
    selectedCells,
    overlappingFaces,
    overlappingMeshCount: overlappingMeshes.size,
  };
}
